import Button from '@/components/atoms/Button'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { drawerCreators } from '@/store/ducks/drawer'
import Icon from '@/components/atoms/Icon'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { matriculateCreators } from '@/store/ducks/matriculate'
import cohortsService from '@/services/cohortsService'
import { modalCreators } from '@/store/ducks/modal'
import { trailsRepository } from '@/repositories'

export default function DrawerCourseRecorded() {
	const {
		drawer: { drawerInfo, disabledOnClose },
	} = useAppSelector((state) => state)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	async function closeModal() {
		await dispatch(drawerCreators.closeDrawer())
	}

	const {
		execute: matriculateRecorded,
		isLoading: isLoadingMatriculateRecorded,
		isSuccess: isSuccessMatriculateRecorded,
	} = cohortsService.matriculateRecorded()

	const handleMatriculatedRecorded = async (cohortId: number) => {
		await dispatch(
			matriculateCreators.setClassSelect({
				id: drawerInfo?.curso_id,
				name: drawerInfo?.name_course || '',
				curso_id: drawerInfo?.curso_id || 0,
				'type-course': 'GRAVADO',
			})
		)

		await matriculateRecorded({ cohortId })

		navigate('/curso')

		dispatch(
			modalCreators.openModal({
				name: 'newJourneyModal',
				disabledOnClose: true,
			})
		)
	}

	useEffect(() => {
		if (isSuccessMatriculateRecorded) {
			closeModal()
			dispatch(trailsRepository.util.invalidateTags(['Trails']))
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessMatriculateRecorded])

	const typeCourse = drawerInfo?.['type-course']?.toUpperCase() ?? ''

	return (
		<div className='w-full h-full flex'>
			<div className='relative flex flex-col z-10 h-full'>
				<div
					id='drawer-content'
					className='flex flex-col gap-4 md:gap-6 lg:gap-8 pt-6 px-5 md:px-10 flex-grow overflow-y-auto'
				>
					<Button
						className='px-0 py-2 bg-transparent max-w-max rounded hover:bg-transparent'
						onClick={() => (disabledOnClose ? null : closeModal())}
					>
						<Icon name='close-modal' width='18px' height='18px' />
						<p className='font-semibold text-white ml-2'>Fechar</p>
					</Button>
					<div className='flex flex-col justify-between flex-grow'>
						<div className='flex flex-col gap-8'>
							<div className='flex flex-col gap-2'>
								{drawerInfo?.name_course && (
									<h2
										className={twMerge(
											classNames(
												'text-5xl leading-10 font-bold w-full max-w-[650px] text-white',
												{
													'text-[44px] leading-10':
														drawerInfo?.name_course?.length >= 8,
													'text-[40px] leading-10':
														drawerInfo?.name_course.length >= 16,
													'text-[38px] leading-10':
														drawerInfo?.name_course.length >= 24,
													'text-4xl leading-10':
														drawerInfo?.name_course.length >= 32,
												}
											)
										)}
									>
										{drawerInfo?.name_course || ''}
									</h2>
								)}
								<p className='text-white leading-6 w-full max-w-[680px]'>
									{drawerInfo?.description_course || ''}
								</p>
							</div>
							<div className='flex flex-col gap-8 pr-6 pb-6 flex-1 overflow-y-auto'>
								<div className='flex flex-col md:flex-row gap-3 md:gap-6 md:items-center'>
									<div className='relative max-w-max rounded-full'>
										<span
											className={twMerge(
												classNames(
													'relative text-[#470028] text-sm font-semibold max-w-max rounded-full px-5 py-1 z-20',
													{
														'text-white':
															drawerInfo?.['type-course']?.toUpperCase() ===
															'AO VIVO',
													}
												)
											)}
										>
											CURSO {drawerInfo?.['type-course']?.toUpperCase()}
										</span>
										<Icon
											name='bg-tag'
											width='100%'
											height='100%'
											className={twMerge(
												classNames(
													'absolute top-0 bottom-0 right-0 h-full text-[#FF7CC6] z-0',
													{
														'text-brand-primary':
															drawerInfo?.['type-course']?.toUpperCase() ===
															'AO VIVO',
													}
												)
											)}
										/>
									</div>
									{drawerInfo?.duration && (
										<div className='flex gap-2'>
											<Icon name='clock-drawer' width='20px' height='20px' />
											<span className='text-white text-sm font-medium'>
												{drawerInfo?.duration || ''} horas
											</span>
										</div>
									)}
									{drawerInfo?.units?.length && (
										<div className='flex gap-2'>
											<Icon name='book-drawer' width='20px' height='20px' />
											<span className='text-white text-sm font-medium'>
												{drawerInfo?.units?.length || ''} unidades
											</span>
										</div>
									)}
								</div>
								<div className='flex flex-col gap-4'>
									<p className='text-2xl text-white font-medium'>
										Sobre o professor
									</p>
									<div className='grid grid-cols-1 md:grid-cols-[minmax(148px,_148px)_1fr] gap-6'>
										<div className='flex items-center justify-start rounded-xl max-w-max overflow-clip'>
											<img
												src={drawerInfo?.foto_professor}
												alt='Emoji'
												className='w-[128px] h-[128px] md:w-[148px] md:h-[148px] rounded-xl'
											/>
										</div>
										<div className='flex flex-col gap-5'>
											<div className='flex flex-col gap-1'>
												<p className='text-lg text-white font-medium'>
													{drawerInfo?.name_teacher || ''}
												</p>
												<p className='text-white/80'>
													{drawerInfo?.description_teacher || ''}
												</p>
											</div>
										</div>
									</div>
								</div>
								{drawerInfo?.about_course?.length && (
									<div className='flex flex-col gap-4'>
										<p className='text-2xl text-white font-medium'>
											Sobre o curso
										</p>

										<div className='flex flex-col gap-4'>
											{drawerInfo.about_course.map((item, key) => (
												<p className='text-white/80' key={key}>
													{item}
												</p>
											))}
										</div>
									</div>
								)}
								{drawerInfo?.units?.length && (
									<div className='flex flex-col gap-4'>
										<p className='text-white uppercase font-semibold'>
											Unidades
										</p>
										<div className='flex flex-col gap-2'>
											{drawerInfo.units.map((unit, key) => (
												<div
													className='flex items-center py-2 px-3 bg-white/5 rounded-lg'
													key={key}
												>
													<span className='text-white font-medium'>
														{key + 1}. {unit}
													</span>
												</div>
											))}
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>

				<div className='flex flex-col bg-zinc-900 border-t border-white/10 px-5 md:px-10 py-4 pb-5 flex-shrink-0'>
					<Button
						className='max-w-max rounded-full px-6'
						onClick={() => {
							if (!drawerInfo?.turma || !drawerInfo?.turma.id) return

							handleMatriculatedRecorded(drawerInfo?.turma.id)
						}}
						isLoading={isLoadingMatriculateRecorded}
						disabled={
							!['GRAVADO', 'AO VIVO'].includes(typeCourse) ||
							!drawerInfo?.turma ||
							!drawerInfo?.turma.id
						}
					>
						<p className='font-semibold text-inherit'>
							Fazer matrícula no curso
						</p>
					</Button>
				</div>
			</div>
		</div>
	)
}
